import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { head } from "@omni/utils/array.utils";
import { ReactElement } from "react";

import { CartItemModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import {
    CTAAction,
    CTAActions
} from "@/redux/modules/api/data/cart/@types/cart.types";
import { CallToAction } from "@/types/service/cart/shared";
import acceptNegativeDeliveryPromiseAction from "@/utils/callToActions/shoppingCart/actions/acceptNegativeDeliveryPromise";
import acceptPositiveDeliveryPromiseAction from "@/utils/callToActions/shoppingCart/actions/acceptPositiveDeliveryPromise";
import changeDelivery from "@/utils/callToActions/shoppingCart/actions/changeDelivery";
import changeECornerStoreAction from "@/utils/callToActions/shoppingCart/actions/changeECornerStore";
import changeQuantitiesAction from "@/utils/callToActions/shoppingCart/actions/changeQuantities";
import changeStoreAction from "@/utils/callToActions/shoppingCart/actions/changeStore";
import changeStoreAndCnCAction from "@/utils/callToActions/shoppingCart/actions/changeStoreAndCnC";
import removeArticleAction from "@/utils/callToActions/shoppingCart/actions/removeArticle";
import baseCTAVisual from "@/utils/callToActions/shoppingCart/visuals/baseCTAVisual";
import changeQuantityCTAVisual from "@/utils/callToActions/shoppingCart/visuals/changeQuantityCTAVisual";
import changeStoreVisual from "@/utils/callToActions/shoppingCart/visuals/changeStore";
import { CallToActionTypes } from "@/utils/enums";
import { underScoreToCamelCase } from "@/utils/textUtils";

export type ExplodedCTA = {
    type: CallToActionTypes;
    parameters: Array<string>;
    sku?: string;
    productParameters: Array<string>;
};

export const explodeCTAProductList = (
    cta: CallToAction
): Array<ExplodedCTA> => {
    return cta.products.map<ExplodedCTA>((p) => ({
        type: cta.type,
        parameters: cta.parameters || [],
        sku: p.sku,
        productParameters: p.parameters || []
    }));
};

export const getAction = (
    ctaType: CallToActionTypes,
    hasProduct: boolean
): {
    closable?: boolean;
    // extended means new things could be rendered as the outcome of the action (CHANGE_STORE => storepicker)
    // meaning that the original cta dialog will be closed, extended components still have access to the CallToACtionsContext
    isExtended?: boolean;
    isSilentAction?: boolean;
    action: CTAAction | null;
    visual?: (
        product: CartItemModel | null,
        action: CTAActions
    ) => ReactElement;
} => {
    const managedCtas = {
        [CallToActionTypes.REMOVE_ARTICLE]: {
            visual: baseCTAVisual,
            action: removeArticleAction
        },
        [CallToActionTypes.REMOVE_ACCESSORY_ARTICLE]: {
            isSilentAction: false,
            visual: undefined,
            action: removeArticleAction
        },
        [CallToActionTypes.CHANGE_TO_HOME_DELIVERY]: {
            visual: baseCTAVisual,
            action: changeDelivery(DeliveryType.HomeDelivery)
        },
        [CallToActionTypes.CHANGE_TO_CNC]: {
            visual: baseCTAVisual,
            action: changeDelivery(DeliveryType.ClickAndCollect)
        },
        [CallToActionTypes.ACCEPT_NEGATIVE_DELIVERY_PROMISE]: {
            visual: baseCTAVisual,
            action: acceptNegativeDeliveryPromiseAction
        },
        [CallToActionTypes.ACCEPT_POSITIVE_DELIVERY_PROMISE]: {
            isSilentAction: true,
            action: acceptPositiveDeliveryPromiseAction
        },
        [CallToActionTypes.CHANGE_QUANTITIES]: {
            visual: changeQuantityCTAVisual,
            action: changeQuantitiesAction
        },
        [CallToActionTypes.CHANGE_STORE]: {
            /* 
                CHANGE_STORE kan terugkomen zonder products, 
                in dit geval moet ineens de action uitgevoerd worden. 
            **/
            isSilentAction: !hasProduct,
            visual: hasProduct ? changeStoreVisual : undefined,
            action: changeStoreAction,
            isExtended: true
        },
        [CallToActionTypes.CHOOSE_STORE]: {
            /* 
                CHOOSE_STORE kan terugkomen zonder products, 
                in dit geval moet ineens de action uitgevoerd worden. 
            **/
            isSilentAction: !hasProduct,
            visual: hasProduct ? changeStoreVisual : undefined,
            action: changeStoreAction,
            isExtended: true
        },
        [CallToActionTypes.CHANGE_STORE_AND_CNC]: {
            visual: baseCTAVisual,
            action: changeStoreAndCnCAction,
            isExtended: true
        },
        [CallToActionTypes.CHANGE_STORE_TO_ECORNER]: {
            isSilentAction: true,
            visual: undefined,
            action: changeECornerStoreAction,
            isExtended: true
        }
    };

    const result = managedCtas[ctaType];

    if (!result) {
        throw Error(`CTA can not be handled - ${ctaType}`);
    }

    return result;
};

export const getKeyForCTA = (ctas: Array<CTAActions>): string => {
    if (
        ctas.some((cta) =>
            [
                CallToActionTypes.ACCEPT_NEGATIVE_DELIVERY_PROMISE,
                CallToActionTypes.CHANGE_QUANTITIES
            ].some((c) => c === cta.type)
        )
    ) {
        return "notEnoughStock";
    }

    return underScoreToCamelCase(head(ctas)!.type);
};
